<template>
  <div class="certificate-container" v-if="enrollment && course">
    <!-- Print Controls (Hidden during print) -->
    <div class="print-controls no-print">
      <button @click="$router.back()" class="btn btn-secondary">
        <i class="fas fa-arrow-left"></i> Back to Course
      </button>
      <button @click="printCertificate" class="btn btn-primary">
        <i class="fas fa-print"></i> Print Certificate
      </button>
      <button @click="downloadPdf" class="btn btn-success" :disabled="downloading">
        <i class="fas fa-file-pdf"></i> {{ downloading ? 'Downloading...' : 'Download PDF' }}
      </button>
    </div>

    <!-- Certificate Card -->
    <div class="certificate-paper" id="certificate-content">
      <div class="certificate-border-outer">
        <div class="certificate-border-inner">
          <!-- Corners -->
          <div class="corner top-left"></div>
          <div class="corner top-right"></div>
          <div class="corner bottom-left"></div>
          <div class="corner bottom-right"></div>

          <div class="certificate-body">
            <!-- Header Logos -->
            <div class="certificate-header">
              <div class="org-logo" v-if="brandingLogo">
                <img :src="brandingLogo" alt="Organization Logo" />
              </div>
              <div class="scav-seal">
                <!-- Placeholder for SCAV Seal/Logo -->
                <div class="seal-icon">
                  <i class="fas fa-award"></i>
                </div>
              </div>
            </div>

            <!-- Title -->
            <div class="title-section">
              <h1 class="main-title">CERTIFICATE</h1>
              <h2 class="sub-title">OF COMPLETION</h2>
              <div class="title-divider"></div>
            </div>

            <!-- Student Info -->
            <div class="recipient-section">
              <p class="certifies-text">This certifies that</p>
              <h3 class="student-name">{{ studentName }}</h3>
              <div class="name-underline"></div>
              <p class="completion-text">has successfully completed the continuing education course</p>
            </div>

            <!-- Course Info -->
            <div class="course-section">
              <h4 class="course-title">{{ course.title }}</h4>
            </div>

            <!-- CE Credits -->
            <div class="credits-section" v-if="course.credit_hours">
              <div class="credits-box">
                <span class="credit-amount">{{ course.credit_hours }}</span>
                <span class="credit-label">Continuing Education Credit{{ course.credit_hours !== 1 ? 's' : '' }}</span>
              </div>
              <p class="race-text" v-if="course.certification_type">
                RACE Approved: {{ course.certification_type }}
              </p>
            </div>

            <!-- Date & Signatures -->
            <div class="footer-section">
              <div class="signature-block">
                <div class="signature-line"></div>
                <p class="signature-name">{{ instructorSignatureName }}</p>
                <p class="signature-title">Course Instructor</p>
              </div>

              <div class="date-block">
                <div class="signature-line"></div>
                <p class="date-value">{{ completionDate }}</p>
                <p class="date-label">Date of Completion</p>
              </div>
            </div>

            <!-- Organization Info -->
            <div class="org-footer">
              <p class="org-name">{{ organizationName }}</p>
              <p class="org-subtitle">South Carolina Association of Veterinarians</p>
              <p class="org-detail">Veterinary Continuing Education</p>
            </div>

            <!-- Verification ID -->
            <div class="verification-section">
              <p class="cert-id">Certificate ID: {{ enrollment.certificate_number }}</p>
              <p class="verify-url">Verify authenticity at: app.praetorianai.io/verify/{{ enrollment.certificate_number }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else-if="loading" class="loading-state">
    <div class="spinner"></div>
    <p>Preparing your certificate...</p>
  </div>
  <div v-else class="error-state">
    <i class="fas fa-exclamation-triangle"></i>
    <p>Unable to load certificate data. Please ensure you have completed the course.</p>
    <button @click="$router.back()" class="btn btn-primary">Go Back</button>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { lmsAPI } from '@/services/lmsAPI';
import whitelabelBranding from '@/services/whitelabelBrandingService';
import { useToast } from 'vue-toastification';

export default {
  name: 'CourseCertificate',
  props: {
    id: {
      type: [String, Number],
      required: true
    }
  },
  setup(props) {
    const router = useRouter();
    const toast = useToast();
    
    const loading = ref(true);
    const enrollment = ref(null);
    const course = ref(null);
    const downloading = ref(false);

    const studentName = computed(() => {
      if (!enrollment.value?.user) return '';
      const user = enrollment.value.user;
      return `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username || user.email;
    });

    const completionDate = computed(() => {
      if (!enrollment.value?.completed_at) return new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      return new Date(enrollment.value.completed_at).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      });
    });

    const brandingLogo = computed(() => whitelabelBranding.getLogo());
    const organizationName = computed(() => course.value?.organization?.name || 'Veterinary Media.ai');

    // Phase 1b — prefer structured instructors (lead) for the signature line;
    // fall back to the legacy instructor_name text for un-migrated courses.
    const instructorSignatureName = computed(() => {
      const list = Array.isArray(course.value?.instructors) ? course.value.instructors : [];
      const lead = [...list].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0))[0];
      if (lead) return lead.user?.full_name || lead.full_name || lead.name || '';
      return course.value?.instructor_name || 'Course Instructor';
    });

    const loadData = async () => {
      loading.value = true;
      try {
        const [courseRes, progressRes] = await Promise.all([
          lmsAPI.getCourse(props.id),
          lmsAPI.getCourseProgress(props.id)
        ]);
        
        course.value = courseRes.data;
        enrollment.value = progressRes.data.enrollment;
        
        if (enrollment.value.status !== 'COMPLETED') {
          toast.warning('Course must be completed to view certificate');
          router.push(`/learning/courses/${props.id}`);
        }
      } catch (error) {
        console.error('Error loading certificate data:', error);
        toast.error('Failed to load certificate information');
      } finally {
        loading.value = false;
      }
    };

    const printCertificate = () => {
      window.print();
    };

    const downloadPdf = async () => {
      downloading.value = true;
      try {
        const response = await lmsAPI.downloadCertificate(props.id);
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        const filename = `Certificate_${course.value.title.replace(/[^a-zA-Z0-9]/g, '_')}.pdf`;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        toast.success('Certificate downloaded successfully!');
      } catch (error) {
        console.error('Download error:', error);
        toast.error('Failed to download PDF certificate');
      } finally {
        downloading.value = false;
      }
    };

    onMounted(loadData);

    return {
      loading,
      enrollment,
      course,
      studentName,
      completionDate,
      brandingLogo,
      organizationName,
      instructorSignatureName,
      printCertificate,
      downloadPdf,
      downloading
    };
  }
};
</script>

<style scoped>
.certificate-container {
  min-height: 100vh;
  padding: 40px 20px;
  background-color: #f3f4f6;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.print-controls {
  margin-bottom: 30px;
  display: flex;
  gap: 15px;
}

/* Certificate Styles */
.certificate-paper {
  background: white;
  width: 1000px; /* Letter landscape approx ratio */
  height: 770px;
  padding: 30px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.1);
  position: relative;
  overflow: hidden;
  color: #1a1a2e;
  font-family: 'Times New Roman', serif;
}

.certificate-border-outer {
  border: 6px solid #00a79d;
  height: 100%;
  width: 100%;
  padding: 10px;
  position: relative;
}

.certificate-border-inner {
  border: 2px solid #2c5aa0;
  height: 100%;
  width: 100%;
  padding: 40px;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

/* Decorative Corners */
.corner {
  position: absolute;
  width: 20px;
  height: 20px;
  background-color: #00a79d;
  border-radius: 50%;
}

.top-left { top: 10px; left: 10px; }
.top-right { top: 10px; right: 10px; }
.bottom-left { bottom: 10px; left: 10px; }
.bottom-right { bottom: 10px; right: 10px; }

.certificate-body {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.certificate-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.org-logo img {
  max-height: 60px;
  max-width: 200px;
  object-fit: contain;
}

.seal-icon {
  font-size: 3rem;
  color: #d97706;
}

.title-section {
  margin-bottom: 40px;
}

.main-title {
  font-size: 4rem;
  font-weight: 900;
  margin: 0;
  color: #2c5aa0;
  letter-spacing: 4px;
}

.sub-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  color: #2c5aa0;
  letter-spacing: 2px;
}

.title-divider {
  width: 400px;
  height: 3px;
  background-color: #00a79d;
  margin: 15px auto;
}

.recipient-section {
  margin-bottom: 30px;
}

.certifies-text {
  font-size: 1.5rem;
  font-style: italic;
  margin-bottom: 20px;
}

.student-name {
  font-size: 3.5rem;
  font-weight: 700;
  color: #2c5aa0;
  margin: 10px 0;
  text-transform: uppercase;
}

.name-underline {
  width: 600px;
  height: 1px;
  background-color: #00a79d;
  margin: 0 auto 20px;
}

.completion-text {
  font-size: 1.4rem;
}

.course-section {
  margin-bottom: 30px;
  max-width: 800px;
}

.course-title {
  font-size: 2.2rem;
  font-weight: 700;
  color: #00a79d;
  margin: 0;
}

.credits-section {
  margin-bottom: 40px;
}

.credits-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 10px;
}

.credit-amount {
  font-size: 2rem;
  font-weight: 700;
  color: #2c5aa0;
}

.credit-label {
  font-size: 1.2rem;
  font-weight: 600;
}

.race-text {
  font-size: 1.1rem;
  font-weight: 600;
  color: #4b5563;
}

.footer-section {
  width: 100%;
  display: flex;
  justify-content: space-around;
  margin-top: auto;
  margin-bottom: 20px;
}

.signature-line {
  width: 250px;
  height: 1px;
  background-color: #1a1a2e;
  margin-bottom: 8px;
}

.signature-name, .date-value {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
}

.signature-title, .date-label {
  font-size: 0.9rem;
  color: #4b5563;
  margin: 0;
}

.org-footer {
  margin-bottom: 20px;
}

.org-name {
  font-size: 1.4rem;
  font-weight: 700;
  color: #2c5aa0;
  margin: 0;
}

.org-subtitle {
  font-size: 1.1rem;
  margin: 2px 0;
}

.org-detail {
  font-size: 1rem;
  font-style: italic;
  margin: 0;
}

.verification-section {
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0 20px;
  font-size: 0.75rem;
  color: #9ca3af;
}

.loading-state, .error-state {
  height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #2c5aa0;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 20px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-state i {
  font-size: 3rem;
  color: #ef4444;
  margin-bottom: 20px;
}

/* Print Styles */
@media print {
  body {
    background: white;
  }
  
  .no-print {
    display: none !important;
  }
  
  .certificate-container {
    padding: 0;
    background: transparent;
  }
  
  .certificate-paper {
    box-shadow: none;
    width: 100%;
    height: 100%;
    page-break-after: always;
  }
}

/* Landscape orientation for print */
@page {
  size: letter landscape;
  margin: 0;
}
</style>

